'use client'

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  io,
  type Socket,
} from 'socket.io-client'

import type {
  RealtimeMessage,
  RealtimeTyping,
} from '@/hooks/useInboxSocket'

type InboxSocketApi = {
  onMessage(
    cb: (m: RealtimeMessage) => void
  ): () => void

  onTyping(
    cb: (t: RealtimeTyping) => void
  ): () => void

  onPresenceInit(
    cb: (onlineUserIds: number[]) => void
  ): () => void

  onPresence(
    cb: (
      userId: number,
      isOnline: boolean
    ) => void
  ): () => void

  onRead(
    cb: (payload: {
      conversationId: number
      userId: number
      messageId: number
    }) => void
  ): () => void

  emitMessage(
    payload: RealtimeMessage
  ): void

  typing(
    conversationId: number,
    isTyping: boolean
  ): void

  read(
    conversationId: number,
    lastReadMessageId: number
  ): void
}

type InboxSocketContextValue = {
  connected: boolean
  presenceReady: boolean
  socket: InboxSocketApi
  onlineIds: Set<number>
  isOnline: (userId: number | null | undefined) => boolean
}

const InboxSocketContext =
  createContext<InboxSocketContextValue | null>(
    null
  )

export function InboxSocketProvider({
  userId,
  children,
}: {
  userId: number | null
  children: React.ReactNode
}) {
  const socketRef =
    useRef<Socket | null>(null)

  const [connected, setConnected] =
    useState(false)

  const [onlineIds, setOnlineIds] =
    useState<Set<number>>(new Set())

  const [presenceReady, setPresenceReady] = useState(false)

  useEffect(() => {
    if (!userId) {
        setConnected(false)
        setOnlineIds(new Set())
        setPresenceReady(false)
        return
    }

    const s = io(
      window.location.origin,
      {
        path: '/socket.io',
        transports: ['websocket'],
        auth: {
          userId,
        },
      }
    )

    socketRef.current = s

    const onConnect = () => {
      setConnected(true)
    }

    const onDisconnect = () => {
      setConnected(false)
    }

    const onPresenceInit = (
      payload: any
    ) => {
      const ids = (
        payload?.onlineUserIds || []
      )
        .map((id: any) =>
          Number(id)
        )
        .filter(Boolean)

      setOnlineIds(
        new Set(ids)
      )
      setPresenceReady(true)
    }

    const onPresence = (
      payload: any
    ) => {
      const id =
        Number(payload?.userId)

      if (!id) return

      const online =
        Boolean(payload?.isOnline)

      setOnlineIds((prev) => {
        const next =
          new Set(prev)

        if (online) {
          next.add(id)
        } else {
          next.delete(id)
        }

        return next
      })
    }

    s.on(
      'connect',
      onConnect
    )

    s.on(
      'disconnect',
      onDisconnect
    )

    s.on(
      'dm:presence:init',
      onPresenceInit
    )

    s.on(
      'dm:presence',
      onPresence
    )

    return () => {
      s.off(
        'connect',
        onConnect
      )

      s.off(
        'disconnect',
        onDisconnect
      )

      s.off(
        'dm:presence:init',
        onPresenceInit
      )

      s.off(
        'dm:presence',
        onPresence
      )

      s.disconnect()

      if (socketRef.current === s) {
        socketRef.current = null
      }
    }
  }, [userId])

  const socket =
    useMemo<InboxSocketApi>(() => {
      return {
        onMessage(cb) {
          const s =
            socketRef.current

          if (!s) {
            return () => {}
          }

          s.on(
            'dm:message',
            cb
          )

          return () => {
            s.off(
              'dm:message',
              cb
            )
          }
        },

        onTyping(cb) {
          const s =
            socketRef.current

          if (!s) {
            return () => {}
          }

          s.on(
            'dm:typing',
            cb
          )

          return () => {
            s.off(
              'dm:typing',
              cb
            )
          }
        },

        onPresenceInit(cb) {
          const handler = (
            payload: any
          ) => {
            cb(
              (
                payload?.onlineUserIds ||
                []
              )
                .map((id: any) =>
                  Number(id)
                )
                .filter(Boolean)
            )
          }

          socketRef.current?.on(
            'dm:presence:init',
            handler
          )

          return () => {
            socketRef.current?.off(
              'dm:presence:init',
              handler
            )
          }
        },

        onPresence(cb) {
          const handler = (
            payload: any
          ) => {
            cb(
              Number(
                payload?.userId
              ),
              Boolean(
                payload?.isOnline
              )
            )
          }

          socketRef.current?.on(
            'dm:presence',
            handler
          )

          return () => {
            socketRef.current?.off(
              'dm:presence',
              handler
            )
          }
        },

        onRead(cb) {
          const handler = (
            payload: any
          ) => {
            cb({
              conversationId:
                Number(
                  payload?.conversationId
                ),
              userId:
                Number(
                  payload?.userId
                ),
              messageId:
                Number(
                  payload?.messageId
                ),
            })
          }

          socketRef.current?.on(
            'dm:read',
            handler
          )

          return () => {
            socketRef.current?.off(
              'dm:read',
              handler
            )
          }
        },

        emitMessage(payload) {
          socketRef.current?.emit(
            'dm:message',
            payload
          )
        },

        typing(
          conversationId,
          isTyping
        ) {
          socketRef.current?.emit(
            'dm:typing',
            {
              conversationId,
              isTyping,
            }
          )
        },

        read(
          conversationId,
          lastReadMessageId
        ) {
          socketRef.current?.emit(
            'dm:read',
            {
              conversationId,
              lastReadMessageId,
            }
          )
        },
      }
    }, [])

  const isOnline = (
    id: number | null | undefined
  ) => {
    if (!id) return false

    return onlineIds.has(
      Number(id)
    )
  }

  const value =
    useMemo(
      () => ({
        connected,
        presenceReady,
        socket,
        onlineIds,
        isOnline,
      }),
      [
        connected,
        presenceReady,
        socket,
        onlineIds,
      ]
    )

  return (
    <InboxSocketContext.Provider
      value={value}
    >
      {children}
    </InboxSocketContext.Provider>
  )
}

export function useGlobalInboxSocket() {
  const context =
    useContext(
      InboxSocketContext
    )

  if (!context) {
    throw new Error(
      'useGlobalInboxSocket must be used inside InboxSocketProvider'
    )
  }

  return context
}